import type { TypedClient } from '@platform/openapi';
import { useTenantDetail } from './catalog/useCatalogQueries.ts';
import { subsidiaryOptions } from './settings/subsidiaries.ts';
import { usePageContext } from './usePageContext.ts';

export const MAIN_COMPANY = 'main';

export type CompanyOption = { readonly value: string; readonly label: string };

type Pickable = Parameters<typeof subsidiaryOptions>[0][number];

export const subsidiaryIdOf = (company: string): string | null => (company === MAIN_COMPANY ? null : company);

export const companyOf = (subsidiaryId: string | null): string => subsidiaryId ?? MAIN_COMPANY;

export const sellsFor = (productSubsidiaryId: string | null, company: string): boolean =>
  company === MAIN_COMPANY || productSubsidiaryId === null || productSubsidiaryId === subsidiaryIdOf(company);

export type Companies = {
  readonly mainLabel: string;
  readonly subsidiaries: readonly Pickable[];
};

export const companyOptionsOf = (companies: Companies, keep: string): readonly CompanyOption[] => [
  { value: MAIN_COMPANY, label: companies.mainLabel },
  ...subsidiaryOptions(companies.subsidiaries, keep),
];

export const companyFilterOptionsOf = (companies: Companies): readonly CompanyOption[] => [
  { value: MAIN_COMPANY, label: companies.mainLabel },
  ...companies.subsidiaries.map((subsidiary) => ({ value: subsidiary.id, label: subsidiary.name })),
];

export const companyNameOf = (companies: Companies, subsidiaryId: string | null): string =>
  companies.subsidiaries.find((subsidiary) => subsidiary.id === subsidiaryId)?.name ?? companies.mainLabel;

export const useCompanies = (api: TypedClient): Companies => {
  const { t } = usePageContext();
  const tenant = useTenantDetail(api).data;
  return {
    mainLabel: t('companies.main', { name: tenant?.name ?? '' }),
    subsidiaries: tenant?.subsidiaries ?? [],
  };
};
